// Leaflet + OpenStreetMap choropleth shared by the Geography tabs.
// Countries with data are shaded; clicking one zooms in and reports it via onSelect.
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { feature } from 'topojson-client';
import worldTopo from '../../assets/Map/world_countries.json';

// Inhabited world, Alaska → New Zealand. Fitting bounds (instead of a fixed zoom)
// fills the container without the grey band beyond the poles.
const WORLD_BOUNDS = [[-50, -165], [75, 190]];
// Leaflet computes a NaN zoom (and throws) when fitting bounds in a 0×0 container,
// e.g. while the Geography tab is hidden with display:none.
const hasSize = (map) => {
  const el = map.getContainer();
  return el.clientWidth > 0 && el.clientHeight > 0;
};
const fitWorld = (map, animate) => {
  if (!hasSize(map)) return;
  map.flyToBounds(WORLD_BOUNDS, { padding: [8, 8], duration: animate ? 0.6 : 0, animate });
};

// The map repeats sideways on wide screens; draw the shading on each copy.
const WRAP_OFFSETS = [-360, 0, 360];

// TopoJSON → GeoJSON once per page load.
const FEATURES = (() => {
  const obj = Object.values(worldTopo.objects || {})[0];
  return obj ? feature(worldTopo, obj).features : [];
})();

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Polygon area in squared degrees (shoelace) — only used to pick the biggest piece.
const ringArea = (ring) => {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
  return Math.abs(a / 2);
};

// Bounds to zoom to. Countries that wrap the globe (USA, Russia, Fiji…) zoom to their largest polygon.
function zoomBounds(layer, geometry, offset = 0) {
  const full = layer.getBounds();
  if (full.getEast() - full.getWest() < 180 || geometry?.type !== 'MultiPolygon') return full;
  let best = null, bestArea = -1;
  geometry.coordinates.forEach(poly => {
    const a = ringArea(poly[0]);
    if (a > bestArea) { bestArea = a; best = poly[0]; }
  });
  return best ? L.latLngBounds(best.map(([lng, lat]) => [lat, lng + offset])) : full;
}

/**
 * @param countries    { [iso3]: { color, name, ... } } — countries to shade
 * @param selectedIso  currently selected country (outlined), or null
 * @param onSelect     (iso | null) => void
 * @param tooltipHtml  (iso, data) => string — trusted HTML; escape user text with escapeHtml
 * @param focus        { isos: string[], n: number } — bump `n` to fly the map:
 *                     fits the listed countries, or the world view when `isos` is empty
 * @param children     overlay content (e.g. the country detail panel)
 */
export default function CountryMap({ countries, selectedIso, onSelect, tooltipHtml, focus, children }) {
  const elRef    = useRef(null);
  const mapRef   = useRef(null);
  const layerRef = useRef(null);       // all wrap copies (removed on redraw)
  const mainLayerRef = useRef(null);   // the un-shifted copy, used for programmatic zoom
  const cbRef    = useRef({ onSelect, tooltipHtml });
  cbRef.current  = { onSelect, tooltipHtml };

  // Create the map once.
  useEffect(() => {
    const map = L.map(elRef.current, {
      worldCopyJump: true,
      minZoom: 1,
      zoomSnap: 0.25,
      maxBounds: [[-85, -540], [85, 540]],
      maxBoundsViscosity: 1,
      zoomControl: true,
    });
    // Hidden at mount → plain view for now; the resize observer below refits once visible.
    if (hasSize(map)) map.fitBounds(WORLD_BOUNDS, { padding: [8, 8] });
    else map.setView([20, 10], 1);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      subdomains: 'abc',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // "World" button to zoom back out.
    const Reset = L.Control.extend({
      options: { position: 'topleft' },
      onAdd() {
        const btn = L.DomUtil.create('button', 'geo-leaflet-reset');
        btn.type = 'button';
        btn.title = 'Back to world view';
        btn.textContent = '⟲ World';
        L.DomEvent.disableClickPropagation(btn);
        L.DomEvent.on(btn, 'click', () => {
          fitWorld(map, true);
          cbRef.current.onSelect?.(null);
        });
        return btn;
      },
    });
    new Reset().addTo(map);

    map.on('click', () => cbRef.current.onSelect?.(null));
    mapRef.current = map;

    // Tabs hide the map with display:none — recompute size when it reappears.
    const ro = new ResizeObserver(() => map.invalidateSize());
    // First real size (e.g. tab was hidden at mount) → refit the world.
    let fitted = hasSize(map);
    const ro2 = new ResizeObserver(() => {
      if (!fitted && elRef.current?.clientWidth > 0 && elRef.current?.clientHeight > 0) { fitted = true; map.invalidateSize(); map.fitBounds(WORLD_BOUNDS, { padding: [8, 8] }); }
    });
    ro2.observe(elRef.current);
    ro.observe(elRef.current);

    return () => { ro.disconnect(); ro2.disconnect(); map.remove(); mapRef.current = null; };
  }, []);

  // (Re)draw the country layer whenever data or selection changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) layerRef.current.remove();

    const makeLayer = (offset) => {
      const layer = L.geoJSON(FEATURES, {
        coordsToLatLng: ([lng, lat]) => L.latLng(lat, lng + offset),
        style: (f) => {
          const c = countries[f.id];
          const sel = f.id === selectedIso;
          if (!c) return { weight: 0.4, color: '#94a3b8', fillOpacity: 0, opacity: 0.6 };
          return {
            fillColor: c.color,
            fillOpacity: sel ? 0.9 : 0.78,
            color: sel ? '#0f172a' : '#ffffff',
            weight: sel ? 2.5 : 0.8,
          };
        },
        onEachFeature: (f, lyr) => {
          const c = countries[f.id];
          if (!c) return;
          lyr.bindTooltip(cbRef.current.tooltipHtml(f.id, c), { sticky: true, className: 'geo-leaflet-tip', direction: 'top' });
          lyr.on('mouseover', () => lyr.setStyle({ fillOpacity: 0.95 }));
          lyr.on('mouseout',  () => layer.resetStyle(lyr));
          lyr.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            if (hasSize(map)) map.flyToBounds(zoomBounds(lyr, f.geometry, offset), { padding: [40, 40], maxZoom: 6, duration: 0.8 });
            cbRef.current.onSelect?.(f.id);
          });
        },
      });
      return layer;
    };

    const copies = WRAP_OFFSETS.map(o => makeLayer(o));
    const group = L.featureGroup(copies).addTo(map);
    mainLayerRef.current = copies[WRAP_OFFSETS.indexOf(0)];
    layerRef.current = group;
  }, [countries, selectedIso]);

  // Programmatic zoom (filters, reset). Runs after the layer effect above.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus || !hasSize(map)) return;
    const wanted = new Set(focus.isos || []);
    if (!wanted.size) { fitWorld(map, true); return; }
    let bounds = null;
    mainLayerRef.current?.eachLayer(lyr => {
      const f = lyr.feature;
      if (!f || !wanted.has(f.id)) return;
      const b = zoomBounds(lyr, f.geometry);
      bounds = bounds ? bounds.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
    });
    if (bounds && bounds.isValid()) map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 6, duration: 0.8 });
  }, [focus?.n]); // eslint-disable-line

  return (
    <div className="geo-leaflet-wrap">
      <div ref={elRef} className="geo-leaflet" />
      {children}
    </div>
  );
}

// ── Country detail panel (overlay on the map) ─────────────────────────────

export function DetailPanel({ title, color, onClose, footer, children }) {
  return (
    <div className="geo-detail" onClick={e => e.stopPropagation()}>
      <div className="geo-detail-head">
        <span className="geo-tooltip-dot" style={{ background: color }} />
        <span className="geo-detail-title">{title}</span>
        <button className="geo-chip-x geo-detail-x" onClick={onClose} title="Close">✕</button>
      </div>
      <div className="geo-detail-body">{children}</div>
      {footer && <div className="geo-detail-foot">{footer}</div>}
    </div>
  );
}

export function DetailStats({ items }) {
  return (
    <div className="geo-summary-grid">
      {items.map(({ label, value }) => (
        <div key={label} className="geo-summary-card">
          <div className="geo-summary-label">{label}</div>
          <div className="geo-summary-val">{value}</div>
        </div>
      ))}
    </div>
  );
}

/** Ranked list with proportional bars. rows: [{ label, count }]. Shows `initial` rows, then "Show more". */
export function TopList({ title, rows, total, initial = 5 }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows?.length) return null;
  const max = rows[0].count || 1;
  const shown = expanded ? rows : rows.slice(0, initial);
  const hidden = rows.length - shown.length;
  return (
    <div className="geo-detail-section">
      <div className="geo-section-label">{title}</div>
      {shown.map((r, i) => (
        <div key={r.label} className="geo-detail-row" title={r.label}>
          <span className="geo-toplist-rank">{i + 1}</span>
          <span className="geo-detail-row-main">
            <span className="geo-detail-row-top">
              <span className="geo-toplist-name">{r.label}</span>
              <span className="geo-toplist-num">
                {r.count.toLocaleString()}
                {total ? ` · ${((r.count / total) * 100).toFixed(1)}%` : ''}
              </span>
            </span>
            <span className="geo-detail-bar"><span style={{ width: `${(r.count / max) * 100}%` }} /></span>
          </span>
        </div>
      ))}
      {rows.length > initial && (
        <button type="button" className="geo-link-btn geo-detail-more" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}

export { escapeHtml };
